import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useUser } from '@/providers/UserProvider';
import { useVideos } from '@/providers/VideosProvider';
import { useBottomBar } from '@/providers/BottomBarProvider';
import { sendRequest } from '@/api';
import { Sheet, SheetContent } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { 
  Film,
  Loader2,
  CheckCircle2,
  Circle,
  X
} from 'lucide-react';
import CrossPostView from '../CrossPostView';
import BottomBar from '../BottomBar';

// Import all subcomponents
import VideoCard from './VideoCard';
import DownloadButton from './DownloadButton';
import AddToAutomationModal from './AddToAutomationModal';
import EmptyState from './EmptyState';
import SyncProgressIndicator from './SyncProgressIndicator';
import AccountFilter from './AccountFilter';
import CreateFlowModal from './CreateFlowModal';
import FlowDetailsSheet from './FlowDetailsSheet';
import Notification from './Notification';
import ScheduleVideosModal from './ScheduleVideosModal';

// Constants
const NAVBAR_HEIGHT = '76px';
const IOS_BREAKPOINTS = {
  iPhone: '390px',
  iPhonePlus: '428px'
};
const IOS_SAFE_AREAS = {
  top: 'env(safe-area-inset-top, 47px)',
  bottom: 'env(safe-area-inset-bottom, 34px)',
};
const SPRING_CONFIG = {
  type: "spring",
  stiffness: 300,
  damping: 30
};
const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: SPRING_CONFIG
  }
};

// Main VideoLibrary component
const VideoLibrary = ({ onMount, onUnmount }) => {
  const navigate = useNavigate();
  const { user, fetchUser } = useUser();
  const { 
    videos, 
    loading,
    loadingMore, 
    pageToken, 
    loadMore,
    fetchVideosForPlatform,
    downloadId,
    progress
  } = useVideos();
  const { setShowBottomBar, setBottomBarError, resetStates } = useBottomBar();
  const isPremiumUser = user?.subscribed || false;
  const [showLibrary, setShowLibrary] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [platform, setPlatform] = useState(user?.platforms?.[0]?.name || null);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [downloadCanaryIndex, setDownloadCanaryIndex] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFlowModal, setShowCreateFlowModal] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [showFlowDetails, setShowFlowDetails] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [syncProgress, setSyncProgress] = useState({});
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activeOplaes, setActiveOplaes] = useState([
    {
      id: 1,
      title: "Peak Hours Posting",
      color: '#7C3AED',
      goal: {
        cadence: 'weekly',
        videos_count: 4,
        current_count: 2
      },
      engagement_rate: 8.5,
      strategy: "Peak Hours",
      videos: []
    },
    {
      id: 2,
      title: "Weekend Content",
      color: '#EC4899',
      goal: {
        cadence: 'weekly',
        videos_count: 3,
        current_count: 1
      },
      engagement_rate: 6.2,
      strategy: "Weekend Boost",
      videos: []
    }
  ]);
  const [selectedOplae, setSelectedOplae] = useState(null);
  const [oplaeFeedback, setOplaeFeedback] = useState({
    type: null,
    message: null,
    details: null
  });
  const [showSidebar, setShowSidebar] = useState(false);
  const scrollPositionRef = useRef(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const contentRef = useRef(null);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullToRefreshRef = useRef(null);
  const startY = useRef(0);
  const [activeTab, setActiveTab] = useState('all');

  // Effect to show/hide batch actions and update sidebar
  useEffect(() => {
    const hasSelectedVideos = selectedVideos.size > 0;
    setShowBatchActions(hasSelectedVideos);
    setShowBottomBar(hasSelectedVideos);
  }, [selectedVideos, setShowBottomBar]);

  // Initial fetch of videos from all platforms
  useEffect(() => {
    const fetchVideos = async () => {
      if (user?.platforms?.length > 0) {
        await Promise.all(user.platforms.map(platform => fetchVideosForPlatform(platform)));
      }
    };
    fetchVideos();
  }, [user?.platforms]);

  // Filter and sort videos
  const filteredVideos = React.useMemo(() => {
    let platformVideos = Object.values(videos).flat();
    
    // Filter by platform
    if (activeTab !== 'all') {
      platformVideos = platformVideos.filter(video => video.platform === activeTab);
    }

    // Filter by account
    if (selectedAccount) {
      platformVideos = platformVideos.filter(video => video.account_uid === selectedAccount);
    }

    return platformVideos.sort((a, b) => {
      switch (sortBy) {
        case 'views':
          const bViews = b.platform === 'instagram' ? (b.metrics?.views || 0) : (b.view_count || 0);
          const aViews = a.platform === 'instagram' ? (a.metrics?.views || 0) : (a.view_count || 0);
          return bViews - aViews;
        case 'likes':
          return (b.metrics?.likes || 0) - (a.metrics?.likes || 0);
        case 'comments':
          return (b.metrics?.comments || 0) - (a.metrics?.comments || 0);
        case 'shares':
          return (b.metrics?.shares || 0) - (a.metrics?.shares || 0);
        case 'date':
        default:
          return new Date(b.posted_at || b.created_at) - new Date(a.posted_at || a.created_at);
      }
    });
  }, [videos, sortBy, selectedAccount, activeTab]);

  // Get platform-specific videos
  const instagramVideos = React.useMemo(() => filteredVideos.filter(video => video.platform === 'instagram'), [filteredVideos]);
  const tiktokVideos = React.useMemo(() => filteredVideos.filter(video => video.platform === 'tiktok'), [filteredVideos]);
  const deviceVideos = React.useMemo(() => filteredVideos.filter(video => video.platform === 'device'), [filteredVideos]);

  // Track if we have more content to load
  const hasMoreContent = React.useMemo(() => {
    return Object.values(pageToken).some(token => token !== null);
  }, [pageToken]);

  // Get platforms to load more content from
  const getPlatformsToLoad = () => {
    return user?.platforms?.filter(p => pageToken[p.uid] !== null) || [];
  };

  // Load more videos
  const loadMoreVideos = () => {
    const platformsToLoad = getPlatformsToLoad();
    platformsToLoad.forEach(platform => {
      loadMore(platform);
    });
  };

  // Handle video selection
  const handleVideoSelect = (video) => {
    if (window.bottomBarResetStates) {
      window.bottomBarResetStates();
    }
    setSelectedVideos(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(video.id)) {
        newSelected.delete(video.id);
      } else {
        newSelected.add(video.id);
      }
      return newSelected;
    });
  };

  // Handle batch cross-post
  const handleBatchCrossPost = () => {
    const selectedVideoObjects = filteredVideos.filter(video => selectedVideos.has(video.id));
    navigate('/cross-post', { 
      state: { 
        batchMode: true,
        selectedVideos: selectedVideoObjects
      }
    });
  };

  // Handle Oplae selection
  const handleOplaeSelect = (oplae) => {
    setSelectedOplae(oplae);
    setShowSidebar(true);
    if (selectedVideos.size > 0) {
      const validation = validateOplaeSelection(oplae, selectedVideos.size);
      setOplaeFeedback(validation);
    } else {
      setOplaeFeedback({
        type: 'info',
        message: 'Select videos to add',
        details: `This Oplae needs ${oplae.goal.videos_count - oplae.goal.current_count} more videos`
      });
    }
  };

  // Handle upload success
  const handleUploadSuccess = () => {
    const platforms = user?.platforms || [];
    platforms.forEach(platform => fetchVideosForPlatform(platform));
  };

  // Clear selections
  const clearSelections = () => {
    setSelectedVideos(new Set());
  };

  return (
    <>
      <div className={cn(
        "min-h-screen bg-[#0B0F15]",
        "pt-[var(--navbar-height,_76px)]",
        "pb-[env(safe-area-inset-bottom,_0px)]",
        "overflow-x-hidden"
      )}>
        {/* Hero Section */}
        <div className="bg-[#151A21] p-3 border-b border-gray-800">
          <div className="max-w-full px-5 pb-2">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 text-transparent bg-clip-text mb-2">
              Video Library
            </h1>
            <p className="text-gray-300 text-base">
              Manage and organize your content across platforms
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="h-full flex flex-col">
          {/* Filter Controls */}
          <div className="flex-shrink-0 sticky z-[4] mt-4 bg-[#0B0F15] p-4 sm:px-6 md:px-8">
            <div className="bg-gray-800 rounded-2xl shadow-sm border border-gray-700/50 overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-2">
                  <div className="w-full sm:w-auto">
                    <AccountFilter
                      platforms={user?.platforms}
                      selectedAccount={selectedAccount}
                      onSelectAccount={setSelectedAccount}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                    />
                  </div>
                  <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                    <select
                      className="h-8 px-3 py-0 bg-gray-900/50 border border-gray-700 rounded-full text-sm text-gray-300
                        focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400
                        transition-all duration-200 flex-shrink-0"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="date">Sort by Date</option>
                      <option value="views">Sort by Views</option>
                      <option value="likes">Sort by Likes</option>
                      <option value="comments">Sort by Comments</option>
                    </select>
                    {filteredVideos.length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => {
                          const allSelected = filteredVideos.every(video => selectedVideos.has(video.id));
                          setSelectedVideos(allSelected ? new Set() : new Set(filteredVideos.map(video => video.id)));
                        }}
                        className={cn(
                          "rounded-full h-8 px-3 flex items-center gap-2 whitespace-nowrap",
                          filteredVideos.every(video => selectedVideos.has(video.id))
                            ? 'bg-purple-500 hover:bg-purple-600 text-white'
                            : 'bg-gray-900/50 hover:bg-gray-800 text-gray-400 border border-gray-700'
                        )}
                      >
                        {filteredVideos.every(video => selectedVideos.has(video.id)) ? (
                          <>
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm hidden sm:inline">Selected All</span>
                            <span className="text-sm sm:hidden">Selected All</span>
                          </>
                        ) : (
                          <>
                            <Circle className="h-4 w-4" />
                            <span className="text-sm hidden sm:inline">Select All</span>
                            <span className="text-sm sm:hidden">Select All</span>
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div ref={contentRef} className="flex-1 overflow-y-auto -mx-4 px-4">
            {loading ? (
              <div className="flex items-center justify-center h-[calc(100vh-var(--navbar-height)-8rem)] p-4">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white mb-4">
                    <Loader2 className="h-6 sm:h-8 w-6 sm:w-8 animate-spin" />
                  </div>
                  <p className="text-gray-400">Loading your creative content...</p>
                </div>
              </div>
            ) : filteredVideos.length === 0 ? (
              <EmptyState
                platform={platform}
                isConnected={user?.platforms?.some(p => p.name === platform)}
                onConnect={() => {}}
                onUploadSuccess={handleUploadSuccess}
              />
            ) : (
              <div className={cn(
                "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 md:gap-8",
                "p-4 sm:px-6 md:px-8",
                showBatchActions ? "mb-[calc(80px+env(safe-area-inset-bottom,34px))]" : "mb-4"
              )}>
                <AnimatePresence>
                  {filteredVideos.map((video, index) => (
                    <motion.div
                      key={video.id}
                      variants={CARD_VARIANTS}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      transition={{ delay: index * 0.05 }}
                    >
                      <VideoCard
                        video={video}
                        isDownloaded={
                          downloadCanaryIndex[video.uid] !== null && 
                          videos[video.uid].indexOf(video) <= downloadCanaryIndex[video.uid]}
                        onSelect={handleVideoSelect}
                        isSelected={selectedVideos.has(video.id)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Load More Button */}
            {hasMoreContent && filteredVideos.length > 0 && (
              <div className={cn(
                "w-full text-center mt-4 sm:mt-8",
                showBatchActions ? "mb-[calc(80px+env(safe-area-inset-bottom,34px))]" : "mb-4"
              )}>
                <Button 
                  onClick={loadMoreVideos}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25 rounded-xl"
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <BottomBar 
          className={cn(
            "sticky bottom-0 left-0 right-0",
            "pb-[env(safe-area-inset-bottom, 34px)]",
            "bg-gray-800/80 backdrop-blur-lg border-t border-gray-700",
            "mt-auto"
          )}
          selectedVideos={new Set(filteredVideos.filter(video => selectedVideos.has(video.id)))}
          clearSelections={clearSelections}
          onCrossPost={handleBatchCrossPost}
          onAutomation={() => setShowAutomationModal(true)}
          errorMessage={setBottomBarError}
          showBatchActions={showBatchActions}
          theme="dark"
        />

        {/* Modals */}
        <Sheet open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
          <SheetContent 
            side="bottom" 
            className="h-[95vh] rounded-t-3xl pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
          >
            {selectedVideo && (
              <CrossPostView 
                video={selectedVideo}
                onClose={(result) => {
                  if (result?.type === 'success') {
                    setSuccessMessage(result.message);
                    if (result.targetPlatform) {
                      fetchVideosForPlatform(result.targetPlatform);
                    }
                  }          
                  setSelectedVideo(null);
                }}
                user={user}
                platforms={user?.platforms}
                onError={setBottomBarError}
                reloadUser={fetchUser}
              />
            )}
          </SheetContent>
        </Sheet>

        <CreateFlowModal
          open={showCreateFlowModal}
          onOpenChange={setShowCreateFlowModal}
          onCreateFlow={(flow) => {
            setFlows([...flows, { ...flow, id: Date.now() }]);
            setNotification({
              type: 'success',
              message: `Created new flow "${flow.name}"`
            });
          }}
        />

        <FlowDetailsSheet
          flow={selectedFlow}
          open={showFlowDetails}
          onOpenChange={setShowFlowDetails}
          onUpdate={(updatedFlow) => {
            setFlows(flows.map(f => f.id === updatedFlow.id ? updatedFlow : f));
          }}
        />

        <ScheduleVideosModal
          open={showScheduleModal}
          onOpenChange={setShowScheduleModal}
          videos={filteredVideos.filter(video => selectedVideos.has(video.id))}
          oplae={selectedOplae}
          onSave={async (settings) => {
            try {
              await sendRequest(`/oplaes/${selectedOplae.id}/videos`, 'POST', {
                videos: Object.entries(settings).map(([videoId, config]) => ({
                  video_id: videoId,
                  ...config
                }))
              });
              
              setActiveOplaes(prev => prev.map(o => 
                o.id === selectedOplae.id 
                  ? { ...o, goal: { ...o.goal, current_count: o.goal.current_count + selectedVideos.size }} 
                  : o
              ));
              
              setSelectedOplae(null);
              setSelectedVideos(new Set());
              setOplaeFeedback(null);
              
              setNotification({
                type: 'success',
                message: `Added and configured ${selectedVideos.size} videos to "${selectedOplae.title}"`
              });
            } catch (error) {
              console.error('Failed to save video settings:', error);
              setNotification({
                type: 'error',
                message: 'Failed to save video settings. Please try again.'
              });
            }
          }}
        />

        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    </>
  );
};

// Export everything
export {
  VideoLibrary as default,
  VideoCard,
  DownloadButton,
  AddToAutomationModal,
  EmptyState,
  SyncProgressIndicator,
  AccountFilter,
  CreateFlowModal,
  FlowDetailsSheet,
  Notification,
  ScheduleVideosModal
}; 